import React, { useState, useEffect } from 'react'
import { JSONVisualizer } from '@edge-stream/app-ui-components-react'
import { useUserStore, useConversationStore, useMessageStore, useFormStore, useSettingsStore } from '@edge-interact/integration'
import { SearchIcon, BarChartIcon, ClipboardIcon, SettingsIcon, ArrowDownIcon, PackageIcon, CheckIcon, CloseIcon } from '@bizfirst/common-react'

interface LogEntry {
  id: string
  timestamp: string
  store: string
  action: string
  details: string
  severity: 'info' | 'debug' | 'warn' | 'error'
}

type ViewMode = 'overview' | 'logs' | 'settings'

const STORAGE_KEY = 'explorer-logs-view-mode'

function getStoredViewMode(): ViewMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && ['overview', 'logs', 'settings'].includes(stored)) {
      return stored as ViewMode
    }
  } catch {}
  return 'overview'
}

function saveViewMode(mode: ViewMode) {
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch {}
}

/**
 * ExplorerLogsPage Component
 *
 * Tracks app state changes from Zustand stores.
 * Monitors: User, Conversation, Message, Form, Settings
 *
 * Features:
 * - Overview of state changes
 * - Real-time logs
 * - Logging configuration
 */
export default function ExplorerLogsPage(): React.ReactElement {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode())
  const [enableLogging, setEnableLogging] = useState(true)
  const [autoScroll, setAutoScroll] = useState(true)
  const [selectedLog, setSelectedLog] = useState<string | null>(null)
  const [logUserStore, setLogUserStore] = useState(true)
  const [logConversationStore, setLogConversationStore] = useState(true)
  const [logMessageStore, setLogMessageStore] = useState(true)
  const [maxLogEntries, setMaxLogEntries] = useState(1000)

  const logsContainer = React.useRef<HTMLDivElement>(null)

  const handleChangeViewMode = (mode: ViewMode) => {
    setViewMode(mode)
    saveViewMode(mode)
  }

  // Store states
  const userState = useUserStore()
  const conversationState = useConversationStore()
  const messageState = useMessageStore()
  const formState = useFormStore()
  const settingsState = useSettingsStore()

  // Monitor stores
  useEffect(() => {
    if (!enableLogging) return

    const addLog = (store: string, action: string, details: any) => {
      const newLog: LogEntry = {
        id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        store,
        action,
        details: typeof details === 'string' ? details : JSON.stringify(details).substring(0, 150),
        severity: 'info',
      }
      setLogs((prev) => [...prev.slice(-(maxLogEntries - 1)), newLog])
    }

    // Log state changes based on settings
    if (logUserStore && userState.user) {
      addLog('User', 'Updated', { userId: userState.user.id, name: userState.user.name })
    }
    if (logConversationStore && Object.keys(conversationState).length > 0) {
      addLog('Conversation', 'Updated', { count: Object.keys(conversationState).length })
    }
    if (logMessageStore && Object.keys(messageState).length > 0) {
      addLog('Message', 'Updated', { count: Object.keys(messageState).length })
    }
  }, [userState, conversationState, messageState, enableLogging, logUserStore, logConversationStore, logMessageStore, maxLogEntries])

  // Auto-scroll logs
  useEffect(() => {
    if (autoScroll && logsContainer.current) {
      logsContainer.current.scrollTop = logsContainer.current.scrollHeight
    }
  }, [logs, autoScroll])

  const totalLogs = logs.length
  const logsByStore = logs.reduce(
    (acc, log) => {
      acc[log.store] = (acc[log.store] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )

  return (
    <div className="page-container" style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      {/* Header */}
      <div className="page-title">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2v20m8-10H4" />
          <path d="M7 7h10M7 12h10M7 17h10" />
        </svg>
        Explorer Logs
      </div>
      <p className="page-description">
        Track application state changes across stores
      </p>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid var(--border)' }}>
        {(['overview', 'logs', 'settings'] as ViewMode[]).map((mode) => (
          <button
            key={mode}
            onClick={() => handleChangeViewMode(mode)}
            style={{
              padding: '10px 16px',
              background: viewMode === mode ? 'var(--primary)' : 'transparent',
              color: viewMode === mode ? 'white' : 'var(--text-primary)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: viewMode === mode ? 600 : 400,
              borderBottom: viewMode === mode ? '2px solid var(--primary)' : 'none',
              transition: 'all 0.2s',
            }}
          >
            {mode === 'overview' && <><BarChartIcon size={14} /> Overview</>}
            {mode === 'logs' && <><ClipboardIcon size={14} /> Logs</>}
            {mode === 'settings' && <><SettingsIcon size={14} /> Settings</>}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {viewMode === 'overview' && (
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                padding: '16px',
                background: 'var(--bg-secondary)',
                borderRadius: '8px',
                border: '1px solid var(--border)',
              }}
            >
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                Total Logs
              </div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--primary)' }}>{totalLogs}</div>
            </div>

            {Object.entries(logsByStore).map(([store, count]) => (
              <div
                key={store}
                style={{
                  padding: '16px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {store}
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>{count}</div>
              </div>
            ))}
          </div>

          <div
            style={{
              padding: '16px',
              background: 'var(--bg-secondary)',
              borderRadius: '8px',
              border: '1px solid var(--border)',
            }}
          >
            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--text-primary)' }}>Status</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: enableLogging ? '#10b981' : '#ef4444',
                }}
              />
              <span style={{ fontSize: '13px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {enableLogging ? <><CheckIcon size={13} /> Logging Active</> : <><CloseIcon size={13} /> Logging Disabled</>}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Last update: {logs.length > 0 ? new Date(logs[logs.length - 1].timestamp).toLocaleTimeString() : 'N/A'}
            </div>
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {viewMode === 'logs' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
          }}
        >
          {/* Logs List */}
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: '600px',
              background: 'var(--bg-secondary)',
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                background: 'var(--bg-secondary)',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>Logs ({logs.length})</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  <input
                    type="checkbox"
                    checked={autoScroll}
                    onChange={(e) => setAutoScroll(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Auto-scroll
                </label>
                <button
                  onClick={() => {
                    setLogs([])
                    setSelectedLog(null)
                  }}
                  style={{
                    padding: '4px 8px',
                    background: 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '11px',
                  }}
                >
                  Clear
                </button>
              </div>
            </div>

            <div
              ref={logsContainer}
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '12px 16px',
                fontFamily: 'monospace',
                fontSize: '11px',
              }}
            >
              {logs.length === 0 ? (
                <div style={{ color: 'var(--text-secondary)', textAlign: 'center', paddingTop: '20px' }}>
                  No logs yet. Enable logging in settings.
                </div>
              ) : (
                logs.map((log) => (
                  <div
                    key={log.id}
                    onClick={() => setSelectedLog(log.id)}
                    style={{
                      marginBottom: '8px',
                      padding: '8px',
                      background: selectedLog === log.id ? 'rgba(0, 212, 255, 0.15)' : 'var(--bg-primary)',
                      borderRadius: '4px',
                      borderLeft: `3px solid ${log.severity === 'error' ? '#ef4444' : log.severity === 'warn' ? '#f59e0b' : '#06b6d4'}`,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      border: selectedLog === log.id ? '1px solid #06b6d4' : '1px solid transparent',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600, color: '#3b82f6' }}>{log.store}</span>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div style={{ color: 'var(--text-primary)', marginBottom: '2px' }}>
                      <strong>{log.action}</strong>
                    </div>
                    <div style={{ color: 'var(--text-secondary)', wordBreak: 'break-all', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.details}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Details Panel */}
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: '600px',
              background: 'var(--bg-secondary)',
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                background: 'var(--bg-secondary)',
                borderBottom: '1px solid var(--border)',
                fontWeight: 600,
                fontSize: '13px',
                color: 'var(--text-primary)',
              }}
            >
              Details
            </div>

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px',
              }}
            >
              {selectedLog ? (
                (() => {
                  const log = logs.find((l) => l.id === selectedLog)
                  if (!log) return null

                  // Parse log details if it's a JSON string
                  let detailsData
                  try {
                    detailsData = typeof log.details === 'string' ? JSON.parse(log.details) : log.details
                  } catch {
                    detailsData = { rawText: log.details }
                  }

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)', marginBottom: '4px' }}>
                          Store
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{log.store}</div>
                      </div>

                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)', marginBottom: '4px' }}>
                          Action
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{log.action}</div>
                      </div>

                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)', marginBottom: '4px' }}>
                          Timestamp
                        </div>
                        <div style={{ fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                          {new Date(log.timestamp).toLocaleString()}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)', marginBottom: '4px' }}>
                          Severity
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '3px',
                            background:
                              log.severity === 'error'
                                ? 'rgba(239, 68, 68, 0.2)'
                                : log.severity === 'warn'
                                  ? 'rgba(245, 158, 11, 0.2)'
                                  : 'rgba(6, 182, 212, 0.2)',
                            color:
                              log.severity === 'error'
                                ? '#ef4444'
                                : log.severity === 'warn'
                                  ? '#f59e0b'
                                  : '#06b6d4',
                          }}
                        >
                          {log.severity}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)', marginBottom: '4px' }}>
                          Details
                        </div>
                        <div style={{ fontSize: '12px' }}>
                          <JSONVisualizer data={detailsData} />
                        </div>
                      </div>
                    </div>
                  )
                })()
              ) : (
                <div style={{ color: 'var(--text-secondary)', textAlign: 'center', paddingTop: '20px' }}>
                  Select a log to see details
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {viewMode === 'settings' && (
        <div style={{ maxWidth: '600px' }}>
          <div
            style={{
              padding: '20px',
              background: 'var(--bg-secondary)',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0',
            }}
          >
            <h3 style={{ margin: '0 0 20px 0', color: 'var(--primary)' }}>Logging Configuration</h3>

            {/* Enable Store Logging */}
            <div style={{ marginBottom: '28px', paddingBottom: '28px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <input
                  type="checkbox"
                  id="enableLogging"
                  checked={enableLogging}
                  onChange={(e) => setEnableLogging(e.target.checked)}
                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <label htmlFor="enableLogging" style={{ cursor: 'pointer', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
                  <BarChartIcon size={14} /> Enable Store Logging
                </label>
              </div>
              <p style={{ margin: '0 0 0 30px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Capture state changes from all configured stores
              </p>
            </div>

            {/* User Store */}
            {enableLogging && (
              <div style={{ marginBottom: '16px', marginLeft: '12px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={logUserStore}
                    onChange={(e) => setLogUserStore(e.target.checked)}
                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                  />
                  <span style={{ fontWeight: 500, fontSize: '13px', color: 'var(--text-primary)' }}>User Store</span>
                </label>
                <p style={{ margin: '0 0 0 28px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Track user profile and authentication changes
                </p>
              </div>
            )}

            {/* Conversation Store */}
            {enableLogging && (
              <div style={{ marginBottom: '16px', marginLeft: '12px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={logConversationStore}
                    onChange={(e) => setLogConversationStore(e.target.checked)}
                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                  />
                  <span style={{ fontWeight: 500, fontSize: '13px', color: 'var(--text-primary)' }}>Conversation Store</span>
                </label>
                <p style={{ margin: '0 0 0 28px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Track conversation and group state changes
                </p>
              </div>
            )}

            {/* Message Store */}
            {enableLogging && (
              <div style={{ marginBottom: '28px', marginLeft: '12px', paddingBottom: '28px', borderBottom: '1px solid var(--border)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={logMessageStore}
                    onChange={(e) => setLogMessageStore(e.target.checked)}
                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                  />
                  <span style={{ fontWeight: 500, fontSize: '13px', color: 'var(--text-primary)' }}>Message Store</span>
                </label>
                <p style={{ margin: '0 0 0 28px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Track message content and metadata updates
                </p>
              </div>
            )}

            {/* Auto-scroll */}
            <div style={{ marginBottom: '28px', paddingBottom: '28px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <input
                  type="checkbox"
                  id="autoScroll"
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <label htmlFor="autoScroll" style={{ cursor: 'pointer', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
                  <ArrowDownIcon size={14} /> Auto-scroll Logs
                </label>
              </div>
              <p style={{ margin: '0 0 0 30px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Automatically scroll to the latest log entries in Logs tab
              </p>
            </div>

            {/* Max Entries */}
            <div style={{ marginBottom: '0px', paddingBottom: '0px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', cursor: 'pointer' }}>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}><PackageIcon size={14} /> Max Log Entries</span>
              </label>
              <div style={{ marginLeft: '12px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Maximum: {maxLogEntries} entries
                </label>
                <input
                  type="range"
                  min="100"
                  max="5000"
                  step="100"
                  value={maxLogEntries}
                  onChange={(e) => setMaxLogEntries(parseInt(e.target.value))}
                  style={{ width: '100%', marginTop: '8px', cursor: 'pointer' }}
                />
                <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Older entries will be removed when limit is reached
                </p>
              </div>
            </div>

            {/* Status */}
            <div
              style={{
                padding: '16px',
                background: 'var(--bg-primary)',
                borderRadius: '8px',
                fontSize: '12px',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border)',
                marginTop: '20px',
              }}
            >
              <strong style={{ color: 'var(--text-primary)' }}>Current Status:</strong>
              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div>
                  {enableLogging ? (
                    <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}><CheckIcon size={14} /> Logging is active</span>
                  ) : (
                    <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}><CloseIcon size={14} /> Logging is disabled</span>
                  )}
                </div>
                <div>Total logs captured: <strong style={{ color: 'var(--text-primary)' }}>{totalLogs}</strong></div>
                {Object.keys(logsByStore).length > 0 && (
                  <div>
                    Stores tracked: <strong style={{ color: 'var(--text-primary)' }}>{Object.keys(logsByStore).join(', ')}</strong>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
